/**
 * mobile-apps.ts — Danh sách "Ứng dụng" của hộp "Thêm" trên điện thoại: MỌI trang của menu chính mà vai trò (navForRole) + quyền xem
 * (PAGE_VIEW_PERM, /me/permissions) cho vào — mỗi trang một ô (biểu tượng + tên), nhóm theo mục menu, gắn cờ "Chỉ trên máy tính"
 * theo mobile-scope.ts (bấm vẫn mở được: màn nhắc dùng máy tính + gửi liên kết). Hàm thuần — có test.
 */
import {
  Bookmark,
  BookOpen,
  Bot,
  ChartPie,
  Contact,
  Folder,
  KeyRound,
  LayoutDashboard,
  Megaphone,
  MessagesSquare,
  Package,
  PlugZap,
  Send,
  Settings,
  UserPlus,
  Users,
  Activity,
  Workflow,
  type LucideIcon,
} from 'lucide-react'
import { PAGE_VIEW_PERM, type NavItem, type NavLeaf } from '@/components/shared/nav-config'
import { desktopOnlyFor } from './mobile-scope'

export interface MobileApp {
  key: string
  label: string
  to: string
  icon: LucideIcon
  /** Tên mục menu chính chứa trang (nhóm ở màn "Tất cả ứng dụng"). */
  group: string
  /** Chỉ dùng được trên máy tính (mobile-scope.ts). */
  desktopOnly: boolean
}

/** Biểu tượng riêng từng trang (mục menu chỉ có một biểu tượng cho cả nhóm). Không có → dùng biểu tượng của nhóm. */
const PAGE_ICON: Record<string, LucideIcon> = {
  '/conversations': MessagesSquare,
  '/customers': Contact,
  '/zns-campaigns': Megaphone,
  '/zalo-friend-campaigns': UserPlus,
  '/zalo-broadcast-campaigns': Send,
  '/automation': Workflow,
  '/dashboard': LayoutDashboard,
  '/analytics': ChartPie,
  '/analytics/saved-reports': Bookmark,
  '/integrations': PlugZap,
  '/team/members': Users,
  '/ai': Bot,
  '/ai/knowledge': BookOpen,
  '/ai/san-pham': Package,
  '/ai/thu-vien': Folder,
  '/ai/theo-doi': Activity,
  '/ai/nguon': KeyRound,
  '/settings': Settings,
}

/** Trang con trùng ý với trang cha (Nhân viên AI → Bắt đầu / Danh sách) — không thành ô riêng. */
const SKIP_PAGES = new Set(['/ai/bat-dau', '/ai/nhan-vien'])

/** `perms` chưa tải (undefined) → chưa lọc theo quyền (máy chủ vẫn chặn khi mở trang). */
export function buildMobileApps(nav: NavItem[], perms?: Set<string>): MobileApp[] {
  const ok = (to: string) => !perms || !PAGE_VIEW_PERM[to] || perms.has(PAGE_VIEW_PERM[to])
  const out: MobileApp[] = []
  const push = (p: { label: string; to: string }, section: NavItem) => {
    if (SKIP_PAGES.has(p.to) || !ok(p.to) || out.some((a) => a.to === p.to)) return
    out.push({
      key: p.to,
      label: p.label,
      to: p.to,
      icon: PAGE_ICON[p.to] ?? section.icon,
      group: section.label,
      desktopOnly: !!desktopOnlyFor(p.to),
    })
  }
  for (const s of nav) {
    if (!s.children?.length) {
      push(s, s)
      continue
    }
    for (const c of s.children as NavLeaf[]) {
      push(c, s)
      for (const g of c.children ?? []) push(g, s)
    }
  }
  return out
}

/**
 * Ô hiện sẵn ở hộp "Thêm": ứng dụng KHÁC các tab dưới (`tabPaths`), ưu tiên dùng được trên điện thoại, rồi tới "chỉ trên máy tính".
 */
export function featuredApps(apps: MobileApp[], tabPaths: string[], count = 4): MobileApp[] {
  const others = apps.filter((a) => !tabPaths.includes(a.to))
  return [...others.filter((a) => !a.desktopOnly), ...others.filter((a) => a.desktopOnly)].slice(0, count)
}

/** Nhóm gom các mục menu chỉ có một trang (Nhắn tin, Khách hàng, Kết nối…) — đỡ mỗi nhóm một hàng lẻ loi. */
export const SINGLE_GROUP = 'Chung'

/** Gom theo nhóm (giữ thứ tự menu) cho màn "Tất cả ứng dụng"; mục menu chỉ một trang dồn vào nhóm "Chung" ở đầu. */
export function groupApps(apps: MobileApp[]): { group: string; apps: MobileApp[] }[] {
  const byGroup: { group: string; apps: MobileApp[] }[] = []
  for (const a of apps) {
    const g = byGroup.find((x) => x.group === a.group)
    if (g) g.apps.push(a)
    else byGroup.push({ group: a.group, apps: [a] })
  }
  const singles = byGroup.filter((g) => g.apps.length === 1).flatMap((g) => g.apps)
  const multi = byGroup.filter((g) => g.apps.length > 1)
  return singles.length ? [{ group: SINGLE_GROUP, apps: singles }, ...multi] : multi
}
